import { StyleSheet, View } from 'react-native';

import {
  ImageAssetWithAltText,
  ListItemLinkPressable,
  PressableProps,
  Skeleton,
  Text,
  XStack,
  YStack,
  tokens,
} from '@fhs/ui';

import { SelectedPicker, useComboSelectionContext } from '../../state/combo-selection';
import { MenuPickerComboSlot, MenuPickerData, SelectedCustomization } from '../../types';
import { formatCentsToDollars } from '../../utils';

type MakeComboSectionProps = {
  enabled: boolean;
  required: boolean;
  isLoading?: boolean;
  combo: MenuPickerData['combo'];
  onEdit: (stepIndex: number) => void;
};

type SlotListItemProps = {
  slot: MenuPickerComboSlot;
  isLastItem: boolean;
  selectedPicker?: SelectedPicker;
  onPress: PressableProps['onPress'];
  disabled: PressableProps['disabled'];
};

const SelectedCustomizations = ({
  customizations,
}: {
  customizations?: SelectedCustomization[];
}) => {
  if (!customizations || customizations.length === 0) {
    return null;
  }

  return (
    <YStack style={{ marginTop: 4, gap: 4 }}>
      {customizations.map((item, index) => (
        <XStack key={index}>
          <Text.Ui size="xs">{item.displayName}</Text.Ui>
          {Boolean(item.upChargeCents) && (
            <Text.Ui size="xs" weight="semibold" style={{ marginLeft: 4 }}>
              + {formatCentsToDollars(item.upChargeCents)}
            </Text.Ui>
          )}
        </XStack>
      ))}
    </YStack>
  );
};

const SlotListItem = ({
  slot,
  disabled,
  isLastItem,
  onPress,
  selectedPicker,
}: SlotListItemProps) => {
  if (selectedPicker) {
    const baseCaloriesDisplay = selectedPicker.baseCalories
      ? `${selectedPicker.baseCalories} Cal`
      : null;

    const hasSelectedCustomizations = Boolean(selectedPicker.selectedCustomizationOptions?.length);

    // We want to use price from selectedPickerAspectOption because it's related to the original item without modifiers.
    // But some items don't have modifiers, and won't have picker aspects (e.g. Brownie, Classic Lemonade)
    const itemPriceCents =
      selectedPicker?.selectedPickerAspectOption?.item?.basePriceCents ??
      selectedPicker.basePriceCents ??
      selectedPicker.priceCents;

    const hasUpsellPriceCents = (itemPriceCents ?? 0) > 0;

    return (
      <ListItemLinkPressable
        image={selectedPicker.image}
        title={selectedPicker.displayName}
        titleStyle={{ fontSize: 14 }}
        subtitle={
          (hasUpsellPriceCents || baseCaloriesDisplay || hasSelectedCustomizations) && (
            <>
              <XStack style={{ gap: 4, marginTop: 4 }}>
                {hasUpsellPriceCents && (
                  <Text.Ui size="xs" weight="semibold">
                    {`+ ${formatCentsToDollars(itemPriceCents ?? 0)}`}
                  </Text.Ui>
                )}
                {hasUpsellPriceCents && baseCaloriesDisplay && <Text.Ui size="xs">•</Text.Ui>}
                {baseCaloriesDisplay && <Text.Ui size="xs">{baseCaloriesDisplay}</Text.Ui>}
              </XStack>
              {selectedPicker.selectedCustomizationOptions && (
                <SelectedCustomizations
                  customizations={selectedPicker.selectedCustomizationOptions}
                />
              )}
            </>
          )
        }
        subtitleStyle={{ fontSize: 12 }}
        isLastItem={isLastItem}
        onPress={onPress}
        disabled={disabled}
      />
    );
  }

  return (
    <ListItemLinkPressable
      image={slot.image as ImageAssetWithAltText}
      title={`Add ${slot.displayName}`}
      titleStyle={{ fontSize: 14 }}
      isLastItem={isLastItem}
      onPress={onPress}
      disabled={disabled}
    />
  );
};

const SlotListItemSkeleton = ({ isLastItem }: { isLastItem: boolean }) => (
  <View
    style={[
      styles.skeletonItem,
      !isLastItem && styles.skeletonItemBorder,
    ]}
  >
    <Skeleton style={styles.skeletonImage} />
    <YStack style={styles.skeletonTextWrapper}>
      <Skeleton style={styles.skeletonTitle} />
      <Skeleton style={styles.skeletonSubtitle} />
    </YStack>
  </View>
);

export function MakeComboSection({
  enabled,
  required,
  isLoading = false,
  combo,
  onEdit,
}: MakeComboSectionProps) {
  const { selectedPickers, setIsEditingPickers } = useComboSelectionContext();

  return (
    <YStack>
      {required ? (
        <XStack style={styles.titleSection}>
          <Text weight="semibold" style={styles.title}>
            {combo?.displayName ?? ''}
          </Text>
        </XStack>
      ) : (
        <XStack style={styles.titleSection}>
          <YStack>
            <Text weight="semibold" style={styles.title}>
              {combo?.displayName ?? ''}
            </Text>
            <Text style={styles.subtitle}>{combo?.description ?? ''}</Text>
          </YStack>
        </XStack>
      )}
      <YStack style={styles.itemsWrapper}>
        {combo?.slots?.map((slot, index) =>
          isLoading ? (
            <SlotListItemSkeleton
              key={slot?.id}
              isLastItem={index === combo?.slots?.length - 1}
            />
          ) : (
            <View
              key={slot?.id}
              style={{ opacity: selectedPickers[slot?.id ?? ''] ? undefined : 0.5 }}
            >
              <SlotListItem
                slot={slot as MenuPickerComboSlot}
                selectedPicker={selectedPickers[slot?.id ?? '']}
                isLastItem={index === combo?.slots?.length - 1}
                disabled={!enabled && !required}
                onPress={() => {
                  onEdit(index);

                  if (selectedPickers[slot?.id ?? '']) {
                    setIsEditingPickers(true);
                  }
                }}
              />
            </View>
          )
        )}
      </YStack>
    </YStack>
  );
}

const styles = StyleSheet.create({
  image: {
    aspectRatio: 1,
    width: 40,
    overflow: 'visible',
  },
  titleSection: {
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    marginBottom: 4,
    lineHeight: 16,
    fontSize: 14,
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 20,
  },
  extraPrice: {
    color: tokens.colors.$disabledText,
    fontSize: 14,
    lineHeight: 24,
  },
  buttonWrapper: {
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemsWrapper: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    marginTop: 12,
  },
  skeletonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  skeletonItemBorder: {
    borderBottomWidth: 1,
    borderBottomColor: tokens.colors.$blackOpacity10,
  },
  skeletonImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  skeletonTextWrapper: {
    flex: 1,
    gap: 6,
  },
  skeletonTitle: {
    width: '55%',
    height: 16,
    borderRadius: 8,
  },
  skeletonSubtitle: {
    width: '30%',
    height: 12,
    borderRadius: 8,
  },
  item: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  itemText: {
    fontSize: 14,
  },
  comboItemsDivisorLine: {
    height: 1,
    width: '100%',
    backgroundColor: tokens.colors.$blackOpacity10,
  },
});
